import { router } from "@inertiajs/react"
import { Check, SkipForward, X } from "@untitledui/icons"
import { useRef, useState } from "react"

import { cx } from "@/components/ui/cx"
import { marksAdminCallPath } from "@/pages/admin/calls/paths"
import type { AgendaMark } from "@/pages/admin/calls/types"
import type { AgendaItemState } from "@/pages/admin/schools/types"

/*
 * The agenda, marked as you talk. Marks are open · done · declined · skipped;
 * they are the coordinator's, timestamped per item key, and the summary
 * scores against them — the transcript never overrides a mark.
 *
 * Desktop: hover a row for the three mark actions. Phone, one-handed at the
 * ear: tap = done (tap again = open), press-and-hold offers declined / skipped.
 * A done tick is do.500, the only green on the screen; declined and skipped
 * stay ink. Open rows carry the provisional ring: not yet covered.
 */
const HOLD_MS = 450

const STATE_WORD: Record<AgendaItemState, string> = {
  open: "",
  done: "done",
  declined: "declined",
  skipped: "skipped",
}

export function countMarks(items: { state: AgendaItemState }[]) {
  const n = { open: 0, done: 0, declined: 0, skipped: 0 }
  for (const i of items) n[i.state] += 1
  return n
}

export function marksLine(items: { state: AgendaItemState }[]) {
  const n = countMarks(items)
  return [
    `${n.done} done`,
    n.declined ? `${n.declined} declined` : null,
    n.skipped ? `${n.skipped} skipped` : null,
    `${n.open} open`,
  ]
    .filter(Boolean)
    .join(" · ")
}

export function AgendaMarks({
  callId,
  items,
  head,
  readOnly = false,
}: {
  callId: number
  items: AgendaMark[]
  head: string
  readOnly?: boolean
}) {
  // Optimistic overlay: a mark shows at once, and stops applying the moment
  // the server's state moves on from the one it was made against.
  const [local, setLocal] = useState<
    Record<string, { from: AgendaItemState; to: AgendaItemState }>
  >({})
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const hold = useRef<number | null>(null)
  const held = useRef(false)

  const stateOf = (item: AgendaMark) => {
    const pending = local[item.id]
    return pending && pending.from === item.state ? pending.to : item.state
  }
  const merged = items.map((i) => ({ ...i, state: stateOf(i) }))

  const mark = (id: string, state: AgendaItemState) => {
    setMenuFor(null)
    const from = items.find((i) => i.id === id)?.state ?? "open"
    setLocal((prev) => ({ ...prev, [id]: { from, to: state } }))
    router.patch(
      marksAdminCallPath(callId),
      { item_id: id, state },
      { preserveScroll: true, preserveState: true, only: ["active_call"] },
    )
  }

  const startHold = (id: string) => {
    held.current = false
    hold.current = window.setTimeout(() => {
      held.current = true
      setMenuFor(id)
    }, HOLD_MS)
  }
  const endHold = () => {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = null
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="eyebrow">{head}</span>
        <span className="notation">{marksLine(merged)}</span>
      </div>
      <ul className="divide-tertiary flex flex-col divide-y">
        {merged.map((item) => {
          const word = STATE_WORD[item.state]
          return (
            <li key={item.id} className="group flex flex-col">
              <div className="flex min-h-11 items-center gap-2">
                <button
                  type="button"
                  disabled={readOnly}
                  aria-pressed={item.state === "done"}
                  aria-label={`${item.label}: ${item.state}`}
                  onClick={() => {
                    if (held.current) {
                      held.current = false
                      return
                    }
                    if (readOnly) return
                    mark(item.id, item.state === "done" ? "open" : "done")
                  }}
                  onPointerDown={() => !readOnly && startHold(item.id)}
                  onPointerUp={endHold}
                  onPointerLeave={endHold}
                  onPointerCancel={endHold}
                  onContextMenu={(e) => e.preventDefault()}
                  className={cx(
                    "flex min-h-11 min-w-0 flex-1 items-center gap-2.5 py-1.5 text-left text-sm",
                    item.state === "done" && "text-secondary",
                    item.state === "open" && "text-primary",
                    (item.state === "declined" || item.state === "skipped") &&
                      "text-tertiary",
                  )}
                >
                  <MarkGlyph state={item.state} />
                  <span className="min-w-0 flex-1 truncate">
                    {item.label}
                    {item.custom && (
                      <span className="notation ml-2">added by you</span>
                    )}
                  </span>
                  {word && <span className="notation flex-none">{word}</span>}
                </button>
                {!readOnly && (
                  <span className="hidden flex-none gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 pointer-fine:flex">
                    <MarkAction
                      label="Done"
                      onPress={() => mark(item.id, "done")}
                      active={item.state === "done"}
                    >
                      <Check className="size-3.5" />
                    </MarkAction>
                    <MarkAction
                      label="Declined"
                      onPress={() => mark(item.id, "declined")}
                      active={item.state === "declined"}
                    >
                      <X className="size-3.5" />
                    </MarkAction>
                    <MarkAction
                      label="Skipped"
                      onPress={() => mark(item.id, "skipped")}
                      active={item.state === "skipped"}
                    >
                      <SkipForward className="size-3.5" />
                    </MarkAction>
                  </span>
                )}
              </div>
              {menuFor === item.id && (
                <div
                  role="group"
                  aria-label={`Mark ${item.label}`}
                  className="flex flex-wrap gap-2 pb-2"
                >
                  {(["declined", "skipped", "open"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => mark(item.id, s)}
                      className="ring-ink-nontext text-primary hover:bg-primary_hover h-11 rounded-lg px-3.5 text-sm font-medium ring-1 ring-inset"
                    >
                      {s[0].toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function MarkGlyph({ state }: { state: AgendaItemState }) {
  switch (state) {
    case "done":
      return (
        <Check aria-hidden="true" className="text-do-500 size-4 flex-none" />
      )
    case "declined":
      return (
        <X aria-hidden="true" className="text-fg-quaternary size-4 flex-none" />
      )
    case "skipped":
      return (
        <SkipForward
          aria-hidden="true"
          className="text-fg-quaternary size-4 flex-none"
        />
      )
    default:
      return (
        <span
          aria-hidden="true"
          className="provisional-ring inline-block size-3.5 flex-none"
        />
      )
  }
}

function MarkAction({
  label,
  active,
  onPress,
  children,
}: {
  label: string
  active: boolean
  onPress: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onPress}
      className={cx(
        "text-fg-quaternary hover:text-fg-primary hover:bg-primary_hover flex size-7 items-center justify-center rounded-md",
        active && "text-fg-primary",
      )}
    >
      {children}
    </button>
  )
}
